import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, Input, TextArea, Typography } from '@/components';
import { ASSETS_BASE_URL } from '@/config';
import { CompanySchema } from '@/features/company/types';
import { ImageUpload } from '@/features/files';
import { nullableStringSetter } from '@/helpers';
import { useCountries, useDevelopmentStatus, useTechnologies } from '@/hooks';

import { useUploadLogo } from '../../hooks';
import { RolesSection } from '../preferences/roles-section';
import { ErrorMessage } from '../preferences/error-message';
import { TechnologiesSection } from '../preferences/technologies-section';
import { CountriesSection } from '../preferences/countries-section';
import { SizeSection } from '../preferences/size-section';
import { DevelopmentStagesSection } from '../preferences/development-stages-section';
import {
  SectionErrors,
  validateRoles,
  validateTechnologies,
  validateCountries,
  validateSolarSize,
  validateSolarStages,
  validateBessSize,
  validateBessStages,
} from '../../helpers/preferences-validation';
import { useForm } from 'react-hook-form';

export type CompanyFormFields = {
  name: string;
  logo: string | null;
  website: string | null;
  description: string | null;
  activity_interests: string[];
  technology_interests: string[];
  other_technology: string;
  countries_interests: string[];
  location_interests: string;
  modal_interests: boolean;
  bess_size: [string, string];
  development_status_interests: string[];
  solar_size: [string, string];
  solar_development_status: string[];
};

export type CompanyFormProps = {
  readonly company: CompanySchema;
  readonly onSubmit: (data: CompanyFormFields) => void;
};

export function CompanyForm({ company, onSubmit }: CompanyFormProps) {
  const { t } = useTranslation(['settings']);

  const [sectionErrors, setSectionErrors] = useState<SectionErrors>({});
  const clearSectionError = (section: keyof SectionErrors) => {
    setSectionErrors((prev) => ({ ...prev, [section]: undefined }));
  };
  const uploadLogo = useUploadLogo({ id: company.id });
  const { options: developmentStatusOptions } = useDevelopmentStatus();

  const options = useMemo(
    () => ({
      defaultValues: {
        name: company?.name ?? '',
        logo: company?.logo ?? null,
        website: company?.website ?? null,
        description: company?.description ?? null,
        activity_interests: company?.activity_interests ?? [],
        technology_interests: company?.technology_interests ?? [],
        other_technology: company?.other_technology ?? '',
        countries_interests: company?.countries_interests ?? [],
        location_interests: company?.location_interests ?? '',
        bess_size: company?.bess_size ?? [],
        development_status_interests: company?.development_status_interests ?? [],
        solar_size: company?.solar_size ?? [],
        solar_development_status: company?.solar_development_status ?? [],
      },
    }),
    [company]
  );

  const { filters: technologies } = useTechnologies();
  const { options: defaultCountries } = useCountries();
  const roles = [
    { value: 'buyer', label: t('Investor') },
    { value: 'seller', label: t('Seller / Developer') },
    { value: 'service-provider', label: t('Service Provider') },
    { value: 'debt-provider', label: t('Debt Provider') },
  ];
  const locationAreas = [
    { value: 'greece', label: t('Greece') },
    { value: 'nordics', label: t('Nordics') },
    { value: 'balkans', label: t('Balkans') },
    { value: 'eastern_europe', label: t('Eastern Europe') },
  ];

  const countries = [...defaultCountries, ...locationAreas].sort((a, b) => {
    const aOther = a.label.toLowerCase().includes('other');
    const bOther = b.label.toLowerCase().includes('other');
    if (aOther !== bOther) return aOther ? 1 : -1;
    return a.label.localeCompare(b.label);
  });;

  const methods = useForm<CompanyFormFields>(options);
  const { formState, register, watch, setValue, handleSubmit } = methods;
  const formValues = watch();

  const isInvestor = formValues.activity_interests?.includes('buyer') ?? false;
  const hasSolarInterest = formValues.technology_interests?.includes('solar') ?? false;
  const hasBessInterest = formValues.technology_interests?.includes('bess') ?? false;

  const validateForm = (): boolean => {
    const rolesResult = validateRoles(formValues);
    const techResult = validateTechnologies(formValues);
    const countriesResult = validateCountries(formValues);

    const errors: SectionErrors = {
      roles: rolesResult.error,
      technologies: techResult.error,
      countries: countriesResult.error,
    };

    let isValid = rolesResult.isValid && techResult.isValid && countriesResult.isValid;

    if (isInvestor && hasSolarInterest) {
      const sizeResult = validateSolarSize(formValues);
      const stagesResult = validateSolarStages(formValues);
      errors.solarSize = sizeResult.error;
      errors.solarStages = stagesResult.error;
      isValid = isValid && sizeResult.isValid && stagesResult.isValid;
    }

    if (isInvestor && hasBessInterest) {
      const sizeResult = validateBessSize(formValues);
      const stagesResult = validateBessStages(formValues);
      errors.bessSize = sizeResult.error;
      errors.bessStages = stagesResult.error;
      isValid = isValid && sizeResult.isValid && stagesResult.isValid;
    }

    setSectionErrors(errors);
    return isValid;
  };

  const handleFormSubmit = (data: CompanyFormFields) => {
    const cleanedData = { ...data };
    if (validateForm()) {
      if (!isInvestor) {
        cleanedData.solar_size = ['', ''];
        cleanedData.solar_development_status = [];
        cleanedData.bess_size = ['', ''];
        cleanedData.development_status_interests = [];
      } else {
        if (!hasSolarInterest) {
          cleanedData.solar_size = ['', ''];
          cleanedData.solar_development_status = [];
        }
        if (!hasBessInterest) {
          cleanedData.bess_size = ['', ''];
          cleanedData.development_status_interests = [];
        }
      }
      onSubmit(cleanedData);
    }
  };

  return (
    <div className="space-y-6">
      <form id="company-form" onSubmit={handleSubmit(handleFormSubmit)}>
        {/* Disconnected "Company info" block. */}
        {false && (
        <>
        <div className="mb-8 space-y-6">
          <Typography as="h3" color="primary" size="title" weight="semibold">
            {t('Company info')}
          </Typography>

          <Field
            id="logo"
            label={t('Logo')}
            error={formState.errors['logo']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <ImageUpload
              baseUrl={ASSETS_BASE_URL}
              value={watch('logo')}
              onSelectFile={(file) => {
                uploadLogo(file, (data) => {
                  setValue('logo', data.id);
                });
              }}
            />
          </Field>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field
              id="name"
              label={t('Name')}
              error={formState.errors['name']}
              delay={0.15}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                id="name"
                color={formState.errors['name'] ? 'error' : 'primary'}
                autoComplete="off"
                registration={register('name', {
                  required: t('This field is required'),
                  setValueAs: nullableStringSetter,
                })}
              />
            </Field>

            <Field
              id="website"
              label={t('Website')}
              error={formState.errors['website']}
              delay={0.3}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                id="website"
                color={formState.errors['name'] ? 'error' : 'primary'}
                placeholder="e.g. https://www.nteaser.com"
                registration={register('website')}
              />
            </Field>
          </div>

          <Field
            id="description"
            label={t('Company description')}
            error={formState.errors['description']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea id="description" placeholder="Type here..." registration={register('description')} />
          </Field>
        </div>
        <hr></hr>
        </>
        )}
        <div className="flex flex-col space-y-4">
          <Typography as="h3" color="primary" size="title" weight="semibold">
            {t('Company interests')}
          </Typography>
          <div className="space-y-6">
            <RolesSection
              roles={roles}
              registration={register('activity_interests', {
                onChange: () => clearSectionError('roles'),
              })}
              selectedRoles={watch('activity_interests')}
              isCompanyForm={true}
            />
            <ErrorMessage error={sectionErrors.roles} />
            <TechnologiesSection
              technologies={technologies}
              registration={register('technology_interests', {
                onChange: () => clearSectionError('technologies'),
              })}
              selectedTechnologies={watch('technology_interests')}
              otherValue={watch('other_technology')}
              onOtherChange={(value) => {
                setValue('other_technology', value);
              }}
              isCompanyForm={true}
            />
            <ErrorMessage error={sectionErrors.technologies} />
            <CountriesSection
              countries={countries}
              registration={register('countries_interests', {
                onChange: () => clearSectionError('countries'),
              })}
              selectedCountries={watch('countries_interests')}
              otherValue={watch('location_interests')}
              onOtherChange={(value) => {
                setValue('location_interests', value);
              }}
              isCompanyForm={true}
            />
            <ErrorMessage error={sectionErrors.countries} />

            {isInvestor && hasBessInterest && (
              <>
                <Typography as="h4" color="primary" size="body-1" weight="semibold">
                  {t('BESS info')}
                </Typography>
                <SizeSection
                  value={watch('bess_size')}
                  onChange={(value) => {
                    setValue('bess_size', value);
                    clearSectionError('bessSize');
                  }}
                  unit="MW"
                  fromRegistration={register('bess_size.0')}
                  toRegistration={register('bess_size.1')}
                  isCompanyForm={true}
                />
                <ErrorMessage error={sectionErrors.bessSize} />
                <DevelopmentStagesSection
                  stages={developmentStatusOptions}
                  registration={register('development_status_interests', {
                    onChange: () => clearSectionError('bessStages'),
                  })}
                  selectedStages={watch('development_status_interests')}
                  isCompanyForm={true}
                  idPrefix="development_status_interests"
                />
                <ErrorMessage error={sectionErrors.bessStages} />
              </>
            )}

            {isInvestor && hasSolarInterest && (
              <>
                <Typography as="h4" color="primary" size="title" weight="semibold">
                  {t('Solar PV info')}
                </Typography>
                <SizeSection
                  value={watch('solar_size')}
                  onChange={(value) => {
                    setValue('solar_size', value);
                    clearSectionError('solarSize');
                  }}
                  unit="MWp"
                  fromRegistration={register('solar_size.0')}
                  toRegistration={register('solar_size.1')}
                  isCompanyForm={true}
                />
                <ErrorMessage error={sectionErrors.solarSize} />
                <DevelopmentStagesSection
                  stages={developmentStatusOptions}
                  registration={register('solar_development_status', {
                    onChange: () => clearSectionError('solarStages'),
                  })}
                  selectedStages={watch('solar_development_status')}
                  isCompanyForm={true}
                  idPrefix="solar_development_status"
                />
                <ErrorMessage error={sectionErrors.solarStages} />
              </>
            )}
          </div>
        </div>
      </form>

      <div className="flex w-full items-center justify-end gap-x-3 overflow-hidden border-t border-neutral-50 pt-6">
        <Button type="submit" form="company-form" size="lg" color="primary">
          {t('Save changes')}
        </Button>
      </div>
    </div>
  );
}
